import {inject, Inject, Injectable, signal} from "@angular/core";
import {HttpClient, HttpParams} from "@angular/common/http";
import {PureOriginalsResultModel} from "../models/PureOriginalsResult-model";
import {PureOriginalMatchingModel} from "../models/PureOriginalMatching-model";
import {MostRequestedMissingTuning} from "../models/Most-Requested-Tunings";
import {NumberOfAllTunings} from "../models/NumberOfAllTunings";
import {SortDirectionValuesModel} from "../models/SortDirectionValues-model";
import {Subscription, Observable} from 'rxjs';
import {Location} from "@angular/common";
import {DeleteDialogComponent} from "../helpers/delete-dialog/delete-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {DeleteFileFromDatabaseService} from "./DeleteFileFromDatabase.service";
import {NotificationService} from "./Notification.service";
import {SearchFilterModel} from "../models/SearchFilter-model";

@Injectable({
    providedIn: 'root'
})

export class PureOriginalResultsService {
    private url = `${this.API_URL}`;

    public pureOriginalResult = signal<PureOriginalsResultModel>(new PureOriginalsResultModel());
    public displayedOriginalColumns = signal<(keyof PureOriginalsResultModel)[]>([]);

    public displayedColumns = signal<(keyof MostRequestedMissingTuning)[]>([]);
    public displayedTableHeaders = signal<string[]>([]);
    public allPureOriginalResults = signal<PureOriginalsResultModel[]>([]);
    public pureOriginalMatching = signal<PureOriginalMatchingModel[]>([]);

    public mostRequestedTunings = signal<MostRequestedMissingTuning[]>([]);
    public numberOfMissingTunings = signal<NumberOfAllTunings>(new NumberOfAllTunings(0, 0));
    public type = signal<string>('');
    public tableTitle = signal<string>('');

    public pageNumber = signal<number>(0);
    public pageSize = signal<number>(50);
    public sortAttribute = signal<string>("id");
    public sortDirectionValues = signal<SortDirectionValuesModel>({active: "id", direction: 'desc'});
    public isLoading = signal<boolean>(false);
    public openAdvanceSettings = signal<boolean>(false);
    public searchString = signal<string>('');
    public selectedFilters = signal<SearchFilterModel[]>([]);
    public isFiltered = signal<boolean>(false);
    public tableInitialized = signal<boolean>(false);
    public routeDriven = signal(false);
    public hasMoreTunings = signal(true);
    public tuningLoadError = signal('');
    public tuningTotal = signal<number | null>(null);
    private tuningRequest?: Subscription;
    private failedPage = 0;


    deleteService = inject(DeleteFileFromDatabaseService);
    notificationService = inject(NotificationService);

    httpClient = inject(HttpClient);
    location = inject(Location);
    dialog = inject(MatDialog);

    constructor(@Inject("API_URL") private API_URL: string) {
        const savedSort = localStorage.getItem('sortDirectionValues');
        if (savedSort) {
            this.sortDirectionValues.set(JSON.parse(savedSort));
        } else {

            this.sortDirectionValues.set({active: "id", direction: 'desc'});
            localStorage.setItem('sortDirectionValues', JSON.stringify(this.sortDirectionValues()));
        }
    }

    openAdvancedSearchSettings() {
        this.openAdvanceSettings.set(!this.openAdvanceSettings());
    }

    getMostRequestedTableHeaders() {
        let objectKeys = Object.keys(new MostRequestedMissingTuning());
        for (let key of objectKeys) {
            this.displayedColumns().push(key as keyof MostRequestedMissingTuning)
        }
        this.displayedTableHeaders.set([...this.displayedColumns()]);
    }

    resetMostRequestedTuningsTable() {
        this.displayedColumns.set([]);
        this.displayedTableHeaders.set([]);
        this.mostRequestedTunings.set([]);
        this.pageSize.set(50);
        this.pageNumber.set(0);
        this.isFiltered.set(false);
        this.selectedFilters.set([]);
    }


    resetTable() {
        this.pageNumber.set(0);
        this.allPureOriginalResults.set([]);
        this.getPureOriginalResults();
    }

    getTableKeysForPureOriginalResults() {
        this.displayedOriginalColumns.set([]);
        this.displayedTableHeaders.set([]);
        const objectKeys = Object.keys(this.pureOriginalResult());
        for (let key of objectKeys) {
            this.displayedOriginalColumns.update(cols => [...cols, key as keyof PureOriginalsResultModel]);
        }
        this.displayedTableHeaders.set([...this.displayedOriginalColumns()]);
    }

    triggerPureOriginalResultsRequest() {
        return this.httpClient.get<{
            content: PureOriginalsResultModel[]
        }>(`${this.url}/pureOriginals/pureOriginalResults`, {
            params: this.sortedParams(this.sortDirectionValues())
        })
    }

    getPureOriginalResults() {
        this.triggerPureOriginalResultsRequest().subscribe(response => {
            this.allPureOriginalResults.update((pureOriginalResults) => [...pureOriginalResults, ...response["content"]]);
        })
    }

    getPureOriginalCompletelyMatchedEcu(ecuBuild: string) {
        this.httpClient.get<PureOriginalMatchingModel[]>(`${this.url}/pureOriginals/pureOriginalMatchingStatsCompletelyTunedWithEcuBuild/${this.pageNumber()}/${this.pageSize()}/${this.sortAttribute()}/${ecuBuild}`).subscribe(response => {
            this.pureOriginalMatching.update((pureOriginalMatching) => [...pureOriginalMatching, ...response]);
        })
    }

    getPureOriginalMatchingStatsByHitRateAndEcuBuild(from: number, to: number, ecuBuild: string) {
        this.httpClient.get<PureOriginalMatchingModel[]>(`${this.url}/pureOriginals/pureOriginalMatchingStatsByEcuBuildAndHitRate/${this.pageNumber()}/${this.pageSize()}/${this.sortAttribute()}/${from}/${to}/${ecuBuild}`).subscribe(response => {
            this.pureOriginalMatching.update((pureOriginalMatching) => [...pureOriginalMatching, ...response]);
        })
    }

    sortedParams(sortedParams: SortDirectionValuesModel) {
        return new HttpParams()
            .set('pageNumber', this.pageNumber())
            .set('pageSize', this.pageSize())
            .set('sortAttribute', sortedParams.active)
            .set('sortDirection', sortedParams.direction);
    }

    getMostRequestedPartialTunings() {
        if (!this.isFiltered() && this.tableInitialized()) this.fetchTuningPage(this.pageNumber());
    }

    getMostRequestedMissingTunings() {
        if (!this.isFiltered() && this.tableInitialized()) this.fetchTuningPage(this.pageNumber());
    }

    getMostRequestedTuningsFiltered(filters: SearchFilterModel[]) {
        return this.httpClient.post<TuningPage>(`${this.url}/pureOriginals/listMostRequestedMissingTuningsFiltered`, filters, {
            params: this.sortedParams(this.sortDirectionValues())
        });
    }

    getMostRequestedPartialTuningFiltered(filters: SearchFilterModel[]) {
        return this.httpClient.post<TuningPage>(`${this.url}/pureOriginals/listMostRequestedPartiallyTuningsFiltered`, filters, {
            params: this.sortedParams(this.sortDirectionValues())
        });
    }

    loadInitialData() { this.reloadTunings(); }

    initializeTableForType(type: string, filtersOverride?: SearchFilterModel[]) {
        this.resetForNewType();
        this.type.set(type);
        this.tableTitle.set(type === 'missing' ? 'Most Requested Missing Tuning Table' : 'Most Requested Partial Tuning Table');
        this.tableInitialized.set(true);
        this.routeDriven.set(filtersOverride !== undefined);
        let filters = filtersOverride ?? [];
        if (filtersOverride === undefined) {
            const key = type === 'missing' ? 'mostRequestedMissingTuningsFilters' : 'mostRequestedPartialTuningsFilters';
            try {
                const saved = JSON.parse(localStorage.getItem(key) || '[]');
                if (Array.isArray(saved)) filters = saved;
            } catch { localStorage.removeItem(key); }
        }
        this.selectedFilters.set(filters);
        this.isFiltered.set(filters.length > 0);
        this.reloadTunings();
    }

    loadFilteredData(filters: SearchFilterModel[]) {
        this.selectedFilters.set(filters);
        this.isFiltered.set(filters.length > 0);
        this.reloadTunings();
    }

    applyFilters(filters: SearchFilterModel[]) {
        if (!this.routeDriven()) {
            const key = this.type() === 'missing' ? 'mostRequestedMissingTuningsFilters' : 'mostRequestedPartialTuningsFilters';
            if (filters.length) localStorage.setItem(key, JSON.stringify(filters));
            else localStorage.removeItem(key);
        }
        this.loadFilteredData(filters);
    }

    private reloadTunings(): void {
        this.tuningRequest?.unsubscribe();
        this.isLoading.set(false);
        this.pageNumber.set(0);
        this.mostRequestedTunings.set([]);
        this.tuningTotal.set(null);
        this.hasMoreTunings.set(true);
        this.fetchTuningPage(0);
    }

    private fetchTuningPage(page: number, append = false): void {
        if (this.isLoading() || !this.tableInitialized()) return;
        this.isLoading.set(true);
        this.tuningLoadError.set('');
        this.failedPage = page;
        const previousPage = this.pageNumber();
        this.pageNumber.set(page);
        // UI enum names are translated to the backend's camelCase filter keys.
        const keys: Record<string, string> = {ECU_BUILD: 'ecuBuild', ECU_PRODUCER: 'ecuProducer',
            VEHICLE_PRODUCER: 'vehicleProducer', VEHICLE_SERIES: 'vehicleSeries', VEHICLE_MODEL: 'vehicleModel', TUNING_NAME: 'tuningName'};
        const filters = this.selectedFilters().map(filter => ({...filter, option: keys[filter.option] || filter.option}));
        const request: Observable<TuningPage> = this.isFiltered()
            ? (this.type() === 'missing' ? this.getMostRequestedTuningsFiltered(filters) : this.getMostRequestedPartialTuningFiltered(filters))
            : this.httpClient.get<TuningPage>(`${this.url}/pureOriginals/${this.type() === 'missing'
                ? 'listAllMostRequestedMissingTuningsSortedByAttribute' : 'listAllMostRequestedPartiallyTuningsSortedByAttribute'}`,
                {params: this.sortedParams(this.sortDirectionValues())});
        this.tuningRequest = request.subscribe({
            next: response => {
                const rows = response.content || [];
                const combined = append ? [...this.mostRequestedTunings(), ...rows] : rows;
                this.mostRequestedTunings.set([...new Map(combined.map(row => [row.id, row])).values()]);
                this.tuningTotal.set(response.totalElements ?? null);
                this.hasMoreTunings.set(response.last !== undefined ? !response.last : rows.length === this.pageSize());
                this.isLoading.set(false);
            },
            error: () => {
                this.pageNumber.set(previousPage);
                this.isLoading.set(false);
                this.tuningLoadError.set('Could not load tunings. Please try again.');
            }
        });
    }

    loadMoreFilteredData() {
        if (this.isLoading() || !this.hasMoreTunings() || this.tuningLoadError()) return;
        this.fetchTuningPage(this.pageNumber() + 1, true);
    }

    retryTunings(): void { this.fetchTuningPage(this.failedPage, this.failedPage > 0); }

    deleteMissingTuningFromTable(id: number, tableName: string) {
        const dialogRef = this.dialog.open(DeleteDialogComponent, {
            width: "350px",
            data: "Are you sure you want to delete?"
        })

        dialogRef.afterClosed().subscribe(confirmed => {
            if (confirmed) {
                this.deleteService.deleteFileFromDatabase(id, tableName).subscribe((response) => {
                    console.log(id, tableName)
                    if (response.status === 200) {
                        this.mostRequestedTunings.update((missing) => missing.filter((tuning) => +tuning.id !== id));
                        this.notificationService.showSuccessMessage('Successfully deleted', `${response.body["message"]}`);
                    }
                }, error => {
                    this.notificationService.showErrorMessage('Something went wrong', `${error.error["message"]}`,);
                });
            }
        })
    }


    getNumberOfPartialTunings() {
        this.httpClient.get<NumberOfAllTunings>(`${this.url}/pureOriginals/numberOfMissingAndPartialTunings`).subscribe(value => {
            this.numberOfMissingTunings.set(value)
        })
    }

    changeSortDirection() { this.reloadTunings(); }

    changeSortingAttributes(sortEvent: SortDirectionValuesModel) {
        localStorage.setItem('sortDirectionValues', JSON.stringify(sortEvent));
        this.sortAttribute.set(sortEvent.active);
        this.sortDirectionValues.set(sortEvent);
        this.pageNumber.set(0);
        this.pageSize.set(50);
        return this.sortedParams(sortEvent);
    }

    changeSortDirectionAndAttributePartialTunings(sortEvent: SortDirectionValuesModel) {
        this.changeSortingAttributes(sortEvent);
        this.reloadTunings();
    }

    changeSortDirectionAndAttributeMissingTunings(sortEvent: SortDirectionValuesModel) {
        this.changeSortingAttributes(sortEvent);
        this.reloadTunings();
    }

    triggerOnScroll() {
        this.increasePageNumber();
        this.getPureOriginalResults();
    }

    onScrollMostRequestedTables() { this.loadMoreFilteredData(); }

    increasePageNumber() {
        this.pageNumber.update((pageNum) => pageNum + 1);
    }

    changePureOriginalResultsTableSortDirection(sortDirectionValues: SortDirectionValuesModel) {
        this.pageNumber.set(0);
        this.sortDirectionValues.set(sortDirectionValues);
        this.triggerPureOriginalResultsRequest().subscribe((response) => this.allPureOriginalResults.set([...response["content"]]));
    }

    resetForNewType() {
        this.tuningRequest?.unsubscribe();
        this.tuningLoadError.set('');
        this.tuningTotal.set(null);
        this.hasMoreTunings.set(true);
        this.routeDriven.set(false);
        this.tableInitialized.set(false);
        this.mostRequestedTunings.set([]);
        this.pageNumber.set(0);
        this.isLoading.set(false);
    }




    backToMainTable() {
        this.location.back();
    }
}


interface TuningPage {content: MostRequestedMissingTuning[]; last?: boolean; totalElements?: number;}
